import React, { useState, useEffect } from 'react';
import { Card, CardBody, Container, Form, Row, Col, Label, Input, FormFeedback, Spinner, Table } from 'reactstrap';
import { Btn } from '../../AbstractElements';
import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import { apiPost } from '../../Utils';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { borderColorData } from '../../Data/UiKits/HelperClasses';

interface GroupedPermission {
    [module: string]: Array<{ id: number; module: string; action: string; slug: string; }>;
}

export default function EditRole() {
    const { hasPermission } = useAuth();
    const canEdit = hasPermission('role-management.edit');
    const navigate = useNavigate();
    const location = useLocation();
    const id = location.state?.id;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
    const [permissions, setPermissions] = useState<GroupedPermission>({});
    const [actionHeaders, setActionHeaders] = useState<string[]>([]);
    const [modulesBySection, setModulesBySection] = useState<any>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<{ name?: string }>({});
    const [permissionError, setPermissionError] = useState(false);

    useEffect(() => {
        if (!id) {
            toast.error("No Role ID provided");
            navigate(`${process.env.PUBLIC_URL}/role`);
            return;
        }
        if (canEdit) fetchData();
        else setLoading(false);
    }, []);

    const fetchData = async () => {
        try {
            const [permsData, roleData]: any = await Promise.all([
                apiPost('role/get_permissions', {}),
                apiPost('role/viewbyid', { id })
            ]);
            setPermissions(permsData.permissions);
            setActionHeaders(permsData.actionHeaders);
            setModulesBySection(permsData.sections);
            setName(roleData.name);
            setDescription(roleData.description || '');
            setSelectedPermissions(roleData.permissions?.map((p: any) => p.id) || []);
        } catch (err) {
            toast.error('Failed to load role data');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (id: number) => {
        setSelectedPermissions(prev => {
            const next = prev.includes(id) ? prev.filter(pId => pId !== id) : [...prev, id];
            if (next.length > 0) setPermissionError(false);
            return next;
        });
    };

    const handleModuleToggle = (module: string) => {
        const moduleIds = (permissions[module] || []).map(p => p.id);
        const allSelected = moduleIds.length > 0 && moduleIds.every(id => selectedPermissions.includes(id));
        if (allSelected) {
            setSelectedPermissions(prev => prev.filter(id => !moduleIds.includes(id)));
        } else {
            setSelectedPermissions(prev => {
                const next = Array.from(new Set([...prev, ...moduleIds]));
                if (next.length > 0) setPermissionError(false);
                return next;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        let hasError = false;
        const newErrors: { name?: string } = {};

        if (!name.trim()) {
            newErrors.name = 'Role name is required.';
            hasError = true;
        }

        if (selectedPermissions.length === 0) {
            setPermissionError(true);
            hasError = true;
            document.getElementById('permissions-matrix-table')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        if (hasError) {
            setErrors(newErrors);
            return;
        }

        // Check if View is selected whenever other actions are selected
        for (const [moduleName, modulePerms] of Object.entries(permissions)) {
            const viewAction = modulePerms.find(p => p.action.toLowerCase() === 'view');
            const hasView = viewAction && selectedPermissions.includes(viewAction.id);

            const otherSelected = modulePerms.some(p => p.action.toLowerCase() !== 'view' && selectedPermissions.includes(p.id));

            if (otherSelected && !hasView) {
                toast.error(`View permission is required for ${moduleName} module`);
                return;
            }
        }

        setSaving(true);
        try {
            await apiPost(`role/update`, {
                id,
                name,
                description,
                permissions: selectedPermissions
            });
            toast.success('Role updated successfully!');
            setTimeout(() => navigate(`${process.env.PUBLIC_URL}/role`), 1000);
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to update role.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Role Management" parent="Master Settings" title="Role Management" />
            <Container fluid>
                <Card className="shadow-sm border-0">
                    <CardBody className="p-4">
                        {!canEdit ? (
                            <div className="text-center py-5 text-muted">You do not have permission to edit Roles.</div>
                        ) : loading ? (
                            <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>
                        ) : (
                            <Form onSubmit={handleSubmit} className="theme-form">
                                <div className="mb-4">
                                    <Label className="fw-bold fs-6">Role Name <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        value={name}
                                        onChange={e => { setName(e.target.value); setErrors({}); }}
                                        placeholder="Enter Role Name"
                                        invalid={!!errors.name}
                                        className="form-control"
                                        autoComplete="off"
                                        style={errors.name ? { border: '1px solid #dc3545' } : {}}
                                    />
                                    <FormFeedback>{errors.name}</FormFeedback>
                                </div>

                                <div className="mt-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="fw-bold fs-6">Permissions Matrix <span className="text-danger">*</span></div>
                                        <div className="text-muted small">Select permissions for this role</div>
                                    </div>

                                    <div
                                        id="permissions-matrix-table"
                                        className="table-responsive"
                                        style={permissionError ? {
                                            border: '1px solid #dc3545',
                                            borderRadius: '6px',
                                            boxShadow: '0 0 0 3px rgba(220,53,69,0.15)',
                                            transition: 'box-shadow 0.2s ease'
                                        } : { border: '2px solid transparent', borderRadius: '6px', transition: 'box-shadow 0.2s ease' }}
                                    >
                                        <Table bordered className="matrix-table mb-0" style={{ fontSize: '13px', textAlign: 'center' }}>
                                            <thead style={{ backgroundColor: '#fcfcfc' }}>
                                                <tr>
                                                    <th className="text-start ps-3" style={{ padding: '12px', minWidth: '180px' }}>Module</th>
                                                    {actionHeaders.map(action => (
                                                        <th key={action} style={{ padding: '12px', minWidth: '80px' }}>{action}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(Object.entries(modulesBySection) as [string, string[]][]).map(([section, moduleNames]) => (
                                                    <React.Fragment key={section}>
                                                        <tr style={{ backgroundColor: '#f0f7ff' }}>
                                                            <td colSpan={actionHeaders.length + 1} className="text-start fw-bold text-primary py-2 ps-3" style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '12px' }}>
                                                                {section}
                                                            </td>
                                                        </tr>
                                                        {moduleNames.map((moduleName: string) => {
                                                            const moduleItems = permissions[moduleName] || [];
                                                            return (
                                                                <tr key={moduleName}>
                                                                    <td className="text-start ps-3 py-2 align-middle">
                                                                        <div className="checkbox checkbox-primary m-0 d-flex align-items-center gap-2">
                                                                            <Input
                                                                                type="checkbox"
                                                                                id={`module-${moduleName}`}
                                                                                checked={moduleItems.length > 0 && moduleItems.every(p => selectedPermissions.includes(p.id))}
                                                                                onChange={() => handleModuleToggle(moduleName)}
                                                                                style={{ borderColor: "#308e87" }}
                                                                            />
                                                                            <Label for={`module-${moduleName}`} className="mb-0 fw-semibold text-muted" style={{ cursor: 'pointer' }}>{moduleName}</Label>
                                                                        </div>
                                                                    </td>
                                                                    {actionHeaders.map(action => {
                                                                        const perm = moduleItems.find(p => 
                                                                            p.action.replace(/_/g, ' ').toLowerCase() === action.toLowerCase() ||
                                                                            p.action.replace(/_/g, '/').toLowerCase() === action.toLowerCase()
                                                                        );
                                                                        return (
                                                                            <td key={action} className="align-middle py-2">
                                                                                {perm ? (
                                                                                    <div className="checkbox checkbox-info m-0 d-flex justify-content-center">
                                                                                        <Input
                                                                                            type="checkbox"
                                                                                            id={`p-${perm.id}`}
                                                                                            checked={selectedPermissions.includes(perm.id)}
                                                                                            onChange={() => handleCheckboxChange(perm.id)}
                                                                                            style={{ borderColor: "#308e87" }}
                                                                                        />
                                                                                        <Label for={`p-${perm.id}`} className="mb-0" />
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="text-muted opacity-25" style={{ fontSize: '18px' }}>—</span>
                                                                                )}
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                                {permissionError && (
                                    <div className="text-danger mt-1" style={{ fontSize: '13px' }}>
                                        Please select at least one permission for this role.
                                    </div>
                                )}

                                <div className="mt-5 d-flex gap-2">
                                    <Btn color="primary" type="submit" disabled={saving} className="px-4">
                                        {saving ? <>Updating <Spinner size="sm" /></> : 'Update'}
                                    </Btn>
                                </div>
                            </Form>
                        )}
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
